import React, { ElementType, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Select,
  HStack,
  VStack,
  Badge,
  Button,
  IconButton,
  Input,
  Flex,
  useColorModeValue,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Card,
  CardBody,
  Stack,
  useToast,
  Skeleton,
  SkeletonText,
  Alert,
  AlertIcon,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Kbd,
  Spacer,
  VisuallyHidden,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Icon,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Pencil,
  Trash2,
  Plus,
  Search,
  Filter,
  Phone,
  Mail,
  User2,
  Calendar,
  SortAsc,
  SortDesc,
  X,
} from "lucide-react";
import { FaWhatsapp, FaFacebook, FaInstagram, FaWhatsappSquare } from "react-icons/fa";
import { getCustomers } from "../hooks/customers";
import { Customer } from "../utils/types";
import DashboardSkeleton from "./DashboardSkeleton";

const MotionTr = motion(Tr);
const MotionCard = motion(Card);

// ---- Helper types
type SortKey = "created_at" | "first_name" | "email";
type SortDir = "asc" | "desc";

// ---- Small helper utilities
const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString() : "N/A";

const useClientPagination = <T,>(rows: T[], pageSize: number) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  useEffect(() => {
    // ensure page remains valid if filter changes
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  return { page, setPage, pageCount, current };
};

const CustomersDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const toast = useToast();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("all"); // placeholder for future use
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // View/Edit/Delete state
  const [viewing, setViewing] = useState<Customer | null>(null);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState<Customer | null>(null);

  // Edit form state
  const [form, setForm] = useState<Partial<Customer>>({});
  const [submitting, setSubmitting] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);

  // Colors
  const subtleBorder = useColorModeValue("gray.200", "whiteAlpha.300");
  const headerBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const tableStripe1 = useColorModeValue("green.50", "whiteAlpha.50");
  const tableStripe2 = useColorModeValue("gray.50", "blackAlpha.300");

  // Keyboard shortcuts — F to focus search, / also focuses search, N to create
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "f" || e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key.toLowerCase() === "n") {
        e.preventDefault();
        navigate(`/organization/${orgId}/customers/new`);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, orgId]);

  // Data loader (keeps your existing logic intact)
  const getCustomersData = async (id: any) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const response = await getCustomers(id);
      if (response.success === true && response.data) {
        setCustomers(response.data);
      } else {
        setCustomers(response.data ?? []);
        if (!response.success) setErrorMsg("Failed to load customers.");
      }
    } catch (e) {
      setErrorMsg("Something went wrong while loading customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCustomersData(orgId);
  }, [orgId]);

  // Derived list
  const filteredSorted = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];

    const filtered = customers.filter((c) => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch = !term
        ? true
        : `${c.first_name ?? ""} ${c.second_name ?? ""} ${c.phone ?? ""} ${c.email ?? ""}`
            .toLowerCase()
            .includes(term);
      const matchesStatus = statusFilter === "all" ? true : true; // reserved for future
      return matchesSearch && matchesStatus;
    });

    // bring "today" to top (as in original), then apply sort within groups
    const todayRows = filtered.filter((c) => c.created_at?.startsWith(today));
    const otherRows = filtered.filter((c) => !c.created_at?.startsWith(today));

    const sorter = (a: Customer, b: Customer) => {
      const dir = sortDir === "asc" ? 1 : -1;
      switch (sortKey) {
        case "first_name":
          return (a.first_name || "").localeCompare(b.first_name || "") * dir;
        case "email":
          return (a.email || "").localeCompare(b.email || "") * dir;
        case "created_at":
        default:
          return (
            ((new Date(a.created_at || 0).getTime() -
              new Date(b.created_at || 0).getTime()) as number) * dir
          );
      }
    };

    todayRows.sort(sorter);
    otherRows.sort(sorter);

    return [...todayRows, ...otherRows];
  }, [customers, searchTerm, statusFilter, sortKey, sortDir]);

  // Pagination (client-side)
  const { page, setPage, pageCount, current } = useClientPagination(filteredSorted, 10);

  // ---- Actions (optimistic local updates; swap with API calls as needed)
  const handleEditOpen = (c: Customer) => {
    setEditing(c);
    setForm({ ...c });
  };
  const handleEditCancel = () => {
    setEditing(null);
    setForm({});
  };
  const handleEditSave = async () => {
    setSubmitting(true);
    // TODO: replace with your API call, keep optimistic approach
    setTimeout(() => {
      setCustomers((prev) =>
        prev.map((p) => (p.id === editing?.id ? { ...(p as Customer), ...(form as Customer) } : p))
      );
      setSubmitting(false);
      toast({
        title: "Customer updated",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      setEditing(null);
    }, 600);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    // TODO: call delete API here
    setCustomers((prev) => prev.filter((p) => p.id !== deleting.id));
    setDeleting(null);
    toast({
      title: "Customer deleted",
      status: "info",
      duration: 2500,
      isClosable: true,
    });
  };

  // Validation (minimal example)
  const errors = {
    first_name: !form.first_name ? "First name is required." : "",
    email:
      form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
        ? "Enter a valid email."
        : "",
  };

  const isFormInvalid = Boolean(errors.first_name || errors.email);

  // Responsive card view for mobile
  const MobileCard: React.FC<{ customer: Customer; index: number }> = ({ customer, index }) => (
    <MotionCard
      key={customer.id}
      variant="outline"
      borderColor={subtleBorder}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
    >
      <CardBody>
        <Stack spacing={2}>
          <HStack justify="space-between" align="start">
            <HStack>
              <Badge colorScheme={customer.created_at?.startsWith(new Date().toISOString().split("T")[0]) ? "green" : "gray"}>
                {formatDate(customer.created_at)}
              </Badge>
              <Text fontSize="sm" color="gray.500">#{index + 1 + (page - 1) * 10}</Text>
            </HStack>
            <HStack>
              <Tooltip label="View">
                <IconButton
                  aria-label="View"
                  icon={<Eye size={16} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`../organization/${orgId}/customers/${customer.id}`)}
                />
              </Tooltip>
              <Tooltip label="Edit">
                <IconButton
                  aria-label="Edit"
                  icon={<Pencil size={16} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`../organization/${orgId}/customers/${customer.id}/edit`)}
                />
              </Tooltip>
              <Tooltip label="Delete">
                <IconButton
                  aria-label="Delete"
                  icon={<Trash2 size={16} />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => setDeleting(customer)}
                />
              </Tooltip>
            </HStack>
          </HStack>

          <Heading size="sm" noOfLines={1}>
            {`${customer?.title ?? ""} ${customer?.first_name ?? ""} ${customer?.second_name ?? ""}`.trim() || "Unnamed"}
          </Heading>
          <HStack spacing={3} wrap="wrap" color="gray.600" fontSize="sm">
            <HStack><Phone size={14} /><Text>{customer.phone || "N/A"}</Text></HStack>
            <HStack><Mail size={14} /><Text noOfLines={1}>{customer.email || "N/A"}</Text></HStack>
          </HStack>

          <HStack spacing={2} pt={1} wrap="wrap">
            {customer.whatsapp && (
              <Button
                as="a"
                href={`https://wa.me/${customer.whatsapp}`}
                target="_blank"
                size="xs"
                colorScheme="green"
                leftIcon={<Icon as={FaWhatsapp as ElementType} />}
              >
                WhatsApp
              </Button>
            )}
            {customer.facebook_id && (
              <Button
                as="a"
                href={`https://facebook.com/${customer.facebook_id}`}
                target="_blank"
                size="xs"
                colorScheme="facebook"
                leftIcon={<Icon as={FaFacebook as ElementType} />}
              >
                Facebook
              </Button>
            )}
            {customer.instagram_id && (
              <Button
                as="a"
                href={`https://instagram.com/${customer.instagram_id}`}
                target="_blank"
                size="xs"
                colorScheme="pink"
                leftIcon={<Icon as={FaInstagram as ElementType} />}
              >
                Instagram
              </Button>
            )}
          </HStack>
        </Stack>
      </CardBody>
    </MotionCard>
  );

  return (
    <Box p={{ base: 4, md: 8 }}>
      {/* Top Bar */}
      <HStack align="center" justify="space-between" flexWrap="wrap" mb={4} gap={2}>
        <Heading size="lg">Customers Management</Heading>
        <HStack spacing={2}>
          <Tooltip label="Create new (N)">
            <Button
              colorScheme="green"
              bg="#006400"
              leftIcon={<Plus size={18} aria-hidden />}
              onClick={() => navigate(`../organization/${orgId}/customers/new`)}
              size="md"
              borderRadius="lg"
              px={6}
              shadow="md"
              data-testid="create-customer"
              as={motion.button}
              whileTap={{ scale: 0.98 }}
            >
              Create New Customer
            </Button>
          </Tooltip>
        </HStack>
      </HStack>

      {/* Loading / Error */}
      {loading && (
        <>
          <DashboardSkeleton />
        </>
      )}
      {!loading && errorMsg && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          {errorMsg}
        </Alert>
      )}

      {/* Filters */}
      {!loading && (
        <Flex mb={4} gap={3} wrap="wrap">
          <InputGroup maxW={{ base: "100%", sm: "320px" }}>
            <InputLeftElement pointerEvents="none">
              <Search size={16} aria-hidden />
            </InputLeftElement>
            <Input
              ref={searchRef}
              placeholder="Search by name, email, phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search customers"
              data-testid="search-customers"
            />
          </InputGroup>

          <Select
            maxW={{ base: "100%", sm: "220px" }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter customers"
          >
            <option value="all">All customers</option>
            {/* Reserved for future tags/segments */}
          </Select>

          <HStack>
            <Tooltip label="Sort key">
              <Select
                maxW="200px"
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                aria-label="Sort by"
              >
                <option value="created_at">Date created</option>
                <option value="first_name">First name</option>
                <option value="email">Email</option>
              </Select>
            </Tooltip>
            <Tooltip label="Toggle sort direction">
              <IconButton
                aria-label="Toggle sort direction"
                icon={sortDir === "asc" ? <SortAsc size={18} /> : <SortDesc size={18} />}
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              />
            </Tooltip>
          </HStack>

          <Spacer />

          <HStack fontSize="xs" color="gray.500">
            <Text>
              Shortcuts: <Kbd>/</Kbd> or <Kbd>F</Kbd> to search · <Kbd>N</Kbd> to create
            </Text>
          </HStack>
        </Flex>
      )}

      {/* Content */}
      {!loading && !errorMsg && (
        <>
          {/* Desktop Table */}
          <Box display={{ base: "none", md: "block" }}>
            <SimpleGrid columns={1}>
              <Table variant="simple" size="md" role="table">
                <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Phone</Th>
                    {/* <Th>Date</Th> */}
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <AnimatePresence initial={false}>
                    {current.map((customer, index) => (
                      <MotionTr
                        key={customer.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        bg={(index % 2 === 0 ? tableStripe1 : tableStripe2) as any}
                      >
                        <Td>
                          <HStack>
                            <User2 size={16} />
                            <Text noOfLines={1}>
                              {`${customer?.title ?? ""} ${customer?.first_name ?? ""} ${customer?.second_name ?? ""}`.trim() || "Unnamed"}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>{customer?.email || "N/A"}</Td>
                        <Td>{customer?.phone || "N/A"}</Td>
                        {/* <Td>{formatDate(customer?.created_at)}</Td> */}
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="View">
                              <IconButton
                                aria-label="View customer"
                                icon={<Eye size={16} />}
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`../organization/${orgId}/customers/${customer.id}`)}
                              />
                            </Tooltip>
                            <Tooltip label="Edit">
                              <IconButton
                                aria-label="Edit customer"
                                icon={<Pencil size={16} />}
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`../organization/${orgId}/customers/${customer.id}/edit`)}
                              />
                            </Tooltip>
                            <Tooltip label="Delete">
                              <IconButton
                                aria-label="Delete customer"
                                icon={<Trash2 size={16} />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => setDeleting(customer)}
                              />
                            </Tooltip>

                            {customer.whatsapp && (
                              <Tooltip label="WhatsApp">
                                <IconButton
                                  as="a"
                                  href={`https://wa.me/${customer.whatsapp}`}
                                  target="_blank"
                                  aria-label="Open WhatsApp chat"
                                  icon={<Icon as={FaWhatsapp as ElementType} />}
                                  size="sm"
                                  colorScheme="green"
                                  variant="outline"
                                />
                              </Tooltip>
                            )}
                            {customer.facebook_id && (
                              <Tooltip label="Facebook">
                                <IconButton
                                  as="a"
                                  href={`https://facebook.com/${customer.facebook_id}`}
                                  target="_blank"
                                  aria-label="Open Facebook profile"
                                  icon={<Icon as={FaFacebook as ElementType} />}
                                  size="sm"
                                  colorScheme="facebook"
                                  variant="outline"
                                />
                              </Tooltip>
                            )}
                            {customer.instagram_id && (
                              <Tooltip label="Instagram">
                                <IconButton
                                  as="a"
                                  href={`https://instagram.com/${customer.instagram_id}`}
                                  target="_blank"
                                  aria-label="Open Instagram profile"
                                  icon={<Icon as={FaInstagram as ElementType} />}
                                  size="sm"
                                  colorScheme="pink"
                                  variant="outline"
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </MotionTr>
                    ))}
                  </AnimatePresence>
                </Tbody>
              </Table>
            </SimpleGrid>
          </Box>

          {/* Mobile Card List */}
          <VStack display={{ base: "flex", md: "none" }} align="stretch" spacing={3}>
            {current.map((c, i) => (
              <MobileCard key={c.id} customer={c} index={i} />
            ))}
          </VStack>

          {/* Pagination */}
          <HStack mt={4} justify="space-between">
            <Text fontSize="sm" color="gray.600">
              Showing {(page - 1) * 10 + 1}-{Math.min(page * 10, filteredSorted.length)} of {filteredSorted.length}
            </Text>
            <HStack>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={page === 1}
                variant="outline"
              >
                Prev
              </Button>
              <Badge px={3} py={1} borderRadius="md">
                Page {page} / {pageCount}
              </Badge>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                isDisabled={page === pageCount}
                variant="outline"
              >
                Next
              </Button>
            </HStack>
          </HStack>
        </>
      )}

      {/* Empty State */}
      {!loading && !errorMsg && filteredSorted.length === 0 && (
        <Card variant="outline" borderStyle="dashed" mt={4}>
          <CardBody>
            <VStack spacing={2}>
              <Text fontSize="lg" fontWeight="semibold" aria-hidden>
                No results
              </Text>
              <Heading size="sm">No customers found</Heading>
              <Text color="gray.600" textAlign="center">
                Try adjusting your search or create a new customer.
              </Text>
              <Button
                colorScheme="green"
                leftIcon={<Plus size={16} aria-hidden />}
                onClick={() => navigate(`/organization/${orgId}/customers/new`)}
              >
                New Customer
              </Button>
            </VStack>
          </CardBody>
        </Card>
      )}


      {/* DELETE confirm */}
      <Modal isOpen={Boolean(deleting)} onClose={() => setDeleting(null)} isCentered trapFocus>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete customer?</ModalHeader>
          <ModalBody>
            <Text>
              This action cannot be undone. You’re about to delete{" "}
              <Badge colorScheme="red">
                {`${deleting?.title ?? ""} ${deleting?.first_name ?? ""} ${deleting?.second_name ?? ""}`.trim() || "this customer"}
              </Badge>.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDelete} leftIcon={<Trash2 size={16} aria-hidden />}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Live region for async ops */}
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {submitting ? "Saving customer…" : ""}
      </VisuallyHidden>
    </Box>
  );
};

export default CustomersDashboard;
